/**
 * The committee-in-session strip.
 *
 * DELIBERATELY THE SAME OBJECT AS THE DRAFT STRIP, down to the class name. The handoff says
 * "reuse the `DraftStrip` idiom exactly", and the reason is not economy of CSS: this app already
 * teaches its managers that a dark bar above the nav with a pulsing dot and one button means
 * something is running and here is the way in. Inventing a second bar for the second live event
 * of the year would make them learn it twice.
 *
 * Docked ON A PHONE through `dock.tsx`, for the reason that file documents — pull-to-refresh
 * transforms `.shell`'s children, which makes them the containing block for every `position:
 * fixed` descendant. On a desktop the bar is not fixed at all: it is a block inside the sticky
 * `.chrome`, where it was written, and portalling it there would drop it to the foot of the page.
 */

import { managerShort } from '../domain/identity.js';
import { committeeSaid, type CommitteeInfo } from '../domain/committeeStrip.js';
import { ElapsedClock } from './DraftStrip.js';
import { dockWhen } from './dock.js';
import { STRIP_FIXED, useMedia } from './useMedia.js';

export function CommitteeStrip(
  { info, chair, onGo }: { info: CommitteeInfo; chair?: string; onGo: () => void },
) {
  const fixed = useMedia(STRIP_FIXED);
  if (!info.live) return null;

  return dockWhen(fixed,
    <div className={`dstrip committee ${info.voted ? 'voted' : ''}`} role="status">
      <span className="tag">
        <span className="dot" aria-hidden="true" />
        COMMITTEE
      </span>

      <span className="said">
        <span className="wide">
          {/* A middle dot, not a second dash: the sentence already has one. */}
          {committeeSaid(info)}
          {info.title && <> · <b>{info.title}</b></>}
        </span>
        <span className="narrow">{committeeSaid(info, true)}</span>
      </span>

      {chair && (
        <span className="gmwho">
          <b>{managerShort(chair)} is chairing</b>
        </span>
      )}

      <span className="act">
        {/* Elapsed since the item opened — see domain/draftStrip.ts on why not a countdown. */}
        <ElapsedClock since={info.since} className="clock" />
        <button type="button" className="go" onClick={onGo}>
          {/*
            THE LABEL FOLLOWS THE STATE, the same way the draft strip's does. Once your vote is in
            the point is no longer to vote, and a button still saying so is asking for a second
            one.
          */}
          {info.voted ? (
            <>
              <span className="wide">Back to the meeting</span>
              <span className="narrow">Meeting</span>
            </>
          ) : (
            <>
              <span className="wide">Go and vote</span>
              <span className="narrow">Vote</span>
            </>
          )}
        </button>
      </span>
    </div>,
  );
}
